import React, { useEffect, useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
    fetchAllDevicesForAlerts, fetchZones, fetchAlertRules, fetchAlertHistory,
} from '../services/api';
import { AlertRule, AlertHistoryEntry, Zone } from '../types';
import { useAuth } from '../context/AuthContext';
import { ui } from '../styles/shared';
import ErrorBanner from './shared/ErrorBanner';
import PageHeader from './shared/PageHeader';
import { SurveillanceDevice } from './surveillance/surveillanceShared';
import MonitorTab from './surveillance/MonitorTab';
import RulesTab from './surveillance/RulesTab';
import HistoryTab from './surveillance/HistoryTab';
import ReportTab from './surveillance/ReportTab';
import AdvancedTab from './surveillance/AdvancedTab';

type Tab = 'monitor' | 'rules' | 'history' | 'reports' | 'advanced';
const TABS: Tab[] = ['monitor', 'rules', 'history', 'reports', 'advanced'];
const isTab = (v: string | null): v is Tab => TABS.includes(v as Tab);

/**
 * Bevakning: flikrad, sidhuvud och felruta. Det som flera flikar behöver
 * (enheter, zoner, regler, historik) laddas här; resten äger varje flik själv.
 *
 * Sidan kan öppnas på en viss flik via URL:en, t.ex. från en pushnotis:
 *   /surveillance?tab=history              – historikfliken
 *   /surveillance?tab=history&device=<ieee> – historiken filtrerad på en enhet
 */
const Surveillance: React.FC = () => {
    const { timezone, isReadOnly } = useAuth();
    const [searchParams, setSearchParams] = useSearchParams();
    const [devices, setDevices] = useState<SurveillanceDevice[]>([]);
    const [zones, setZones] = useState<Zone[]>([]);
    const [rules, setRules] = useState<AlertRule[]>([]);
    const [history, setHistory] = useState<AlertHistoryEntry[]>([]);
    const [error, setError] = useState<string | null>(null);
    const tabParam = searchParams.get('tab');
    const tab: Tab = isTab(tabParam) ? tabParam : 'monitor';
    const focusDevice = searchParams.get('device');

    // Fliken ligger i URL:en så att en länk dit fungerar och bakåtknappen gör rätt.
    // Enhetsfiltret hör till historiken och släpps när man byter flik.
    const setTab = (next: Tab) => setSearchParams(
        next === 'monitor' ? {} : { tab: next },
        { replace: true },
    );
    const clearFocusDevice = () => setSearchParams({ tab: 'history' }, { replace: true });

    const load = useCallback(async () => {
        try {
            const [resDevices, resZones, resRules, resHistory] = await Promise.allSettled([
                fetchAllDevicesForAlerts(), fetchZones(), fetchAlertRules(), fetchAlertHistory(50),
            ]);

            if (resDevices.status === 'fulfilled') setDevices(resDevices.value);
            if (resZones.status === 'fulfilled')   setZones(resZones.value);
            if (resRules.status === 'fulfilled')   setRules(resRules.value);
            if (resHistory.status === 'fulfilled') setHistory(resHistory.value);

            // Visa bara det första felet – sidan visas ändå med det som gick att hämta
            const firstFail = [resDevices, resZones, resRules, resHistory]
                .find(r => r.status === 'rejected') as PromiseRejectedResult | undefined;
            setError(firstFail ? (firstFail.reason?.message || 'En del data kunde inte hämtas') : null);
        } catch (e: any) {
            setError(e.message || 'Kunde inte hämta bevakningsdata');
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const tabs = ([
        { key: 'monitor', label: '📊 Översikt' },
        { key: 'rules', label: `⚡ Regler (${rules.length})` },
        { key: 'history', label: `📋 Historik (${history.length})` },
        { key: 'reports', label: '📄 Rapporter' },
        { key: 'advanced', label: '⚙️ Avancerat' },
    ] as { key: Tab; label: string }[]).filter(t => !isReadOnly || t.key !== 'rules');

    return (
        <div>
            <PageHeader icon="🔔" title="Bevakning" description="Följ värden i realtid, sätt larmregler och se larmhistorik och rapporter." />
            {error && <ErrorBanner message={error} onDismiss={() => setError(null)} />}

            <div className="surveillance-tabs" style={{ gap: '4px' }}>
                {tabs.map(t => (
                    <button key={t.key} onClick={() => setTab(t.key)} style={ui.tab(tab === t.key)}>
                        {t.label}
                    </button>
                ))}
            </div>

            {/* Översikten hålls monterad men dold så att valda enheter och diagram finns kvar vid flikbyte. */}
            <div hidden={tab !== 'monitor'}>
                <MonitorTab
                    zones={zones}
                    devices={devices}
                    timezone={timezone}
                    active={tab === 'monitor'}
                    onError={setError}
                />
            </div>

            {tab === 'rules' && (
                <RulesTab
                    rules={rules}
                    setRules={setRules}
                    devices={devices}
                    zones={zones}
                    onReload={load}
                    onError={setError}
                />
            )}

            {tab === 'history' && (
                <HistoryTab
                    history={history}
                    timezone={timezone}
                    onRefresh={load}
                    focusDevice={focusDevice}
                    onClearFocus={clearFocusDevice}
                />
            )}

            {tab === 'reports' && (
                <ReportTab devices={devices} />
            )}

            {tab === 'advanced' && (
                <AdvancedTab devices={devices} timezone={timezone} isReadOnly={isReadOnly} />
            )}
        </div>
    );
};

export default Surveillance;
